<template>
  <div class="device-method-column">
    <template>
      <template v-if="(device && device.id) || method || column">
        <template v-if="device && device.id">
          <div class="device-method-column__device">
            <div
              class="device-method-column__label"
              :class="{
                'device-method-column__label--wide': postprocessings,
              }"
            >
              Device
            </div>
            <template v-if="disableClickAway">{{ device.name }}</template>
            <div v-else class="device-method-column__wrapper-link-device">
              <RouterLink :to="linkToDevice" style="display: inline">{{ device.name }}</RouterLink>
            </div>
            <span v-if="vial" class="ml-2" style="flex: none">Vial {{ displayVial }}</span>
          </div>
        </template>
        <template v-if="method">
          <MethodModal
            v-if="methodConfiguration && isMethodModalOpen"
            :isOpen="true"
            :method="method"
            :methodOverride="parsedMethodOverride"
            :deviceConfiguration="methodConfiguration"
            :isReadOnly="true"
            @close="handleMethodModalClose"
          />

          <div style="display: flex; width: fit-content; max-width: 100%; align-items: center">
            <div
              class="device-method-column__label"
              :class="{
                'device-method-column__label--wide': postprocessings,
              }"
              @click="showPreviewModal"
            >
              Method
            </div>
            <PopupInfoMethodColumn
              :method="method"
              :methodOverride="methodOverride"
              :isDisabled="methodExpanded"
              class="device-method-column__link"
              @click="showPreviewModal"
            >
              <a @click="methodExpanded = !methodExpanded"> #{{ method.id }} {{ method.name }} </a>
            </PopupInfoMethodColumn>
            <PopupHelper
              v-if="methodOverride"
              text="This method was modified for this injection. Modified fields are highlighted in the details."
            >
              <div class="device-method-column__modified">modified</div>
            </PopupHelper>
            <BtnIcon
              type="transparent"
              iconMaterial="my_library_books"
              class="device-method-column__btn-details"
              @click="showPreviewModal"
            />
            <BtnIcon
              v-if="!isInSharedMode"
              title="Open in new page"
              iconMaterial="open_in_new"
              class="device-method-column__btn-open-in-new"
              @click="openMethodPreviewPage"
            />
          </div>
          <InfoMethod
            v-if="methodExpanded"
            :method="method"
            :methodOverride="methodOverride"
            bgColor="#ecefff"
            style="margin: 8px 0"
            @click="showPreviewModal"
          />
        </template>
        <template v-if="column">
          <div class="device-method-column__column-title">
            <div
              class="device-method-column__label"
              :class="{
                'device-method-column__label--wide': postprocessings,
              }"
            >
              Column
            </div>
            <PopupInfoMethodColumn
              :column="column"
              :isDisabled="columnExpanded"
              class="device-method-column__link"
            >
              <a @click="columnExpanded = !columnExpanded">
                {{ column.name }}
              </a>
            </PopupInfoMethodColumn>
            <BtnIcon
              v-if="!isInSharedMode"
              title="Open in new page"
              iconMaterial="open_in_new"
              class="device-method-column__btn-open-in-new"
              @click="openColumnPage"
            />
          </div>
          <InfoColumn
            v-if="columnExpanded"
            :column="column"
            bgColor="#ecefff"
            style="margin: 8px 0"
          />
        </template>
        <template v-if="isShowSequenceInfo">
          <div class="device-method-column__column-title">
            <div
              class="device-method-column__label"
              :class="{
                'device-method-column__label--wide': postprocessings,
              }"
            >
              Sequence
            </div>
            <RouterLink :to="linkToSequence">
              {{ sample.sequence.name }}
            </RouterLink>
          </div>
        </template>
      </template>
    </template>

    <template v-if="!device && vial">
      <div class="device-method-column__column-title">
        <div class="device-method-column__label device-method-column__label--wide">Vial</div>
        {{ displayVial }}
      </div>
    </template>

    <template v-if="postprocessings">
      <div class="device-method-column__column-title">
        <div class="device-method-column__label device-method-column__label--wide">
          Postprocessing
        </div>
        <span
          v-for="(postprocessing, index) of postprocessings"
          :key="index"
          class="device-method-column__measurement"
        >
          <span v-if="index !== 0">, </span>
          <a style="display: inline" @click="togglePostprocessing(postprocessing.measurement.id)">
            {{ postprocessing.measurement.type }} - {{ postprocessing.measurement.waveLength }}nm
          </a>
        </span>
      </div>
      <AppliedPostprocessing
        v-for="postprocessing of postprocessingsToShow"
        :key="postprocessing.name"
        :postprocessing="postprocessing"
        class="device-method-column__postprocessing-details"
      />
    </template>

    <template v-if="availablePostprocessingsToApply.length > 0">
      <div class="device-method-column__column-title">
        <div class="device-method-column__label device-method-column__label--wide">
          Postprocessing
        </div>
        <span
          v-for="([channel], index) of availablePostprocessingsToApply"
          :key="index"
          class="device-method-column__measurement"
        >
          <span v-if="index !== 0">, </span>
          <a style="display: inline" @click="togglePostprocessing(channel)">
            {{ getChannelName(channel) }}
          </a>

          <BtnIcon
            v-if="isCalibrationSequence && availablePostprocessingsToApply.length === 1"
            size="16"
            iconMaterial="edit"
            class="device-method-column__btn-edit-postprocessing"
            @click="$emit('editPostprocessing')"
          />
        </span>
      </div>

      <PostprocessingToApply
        v-for="[channel, postprocessing] of postprocessingsToApplyToShow"
        :key="channel"
        :postprocessing="postprocessing"
        :channel="channel"
        :title="getChannelName(channel)"
        class="device-method-column__postprocessing-details"
      />
    </template>
  </div>
</template>

<script>
  import BtnIcon from '@/uikitBase/btns/BtnIcon';
  import { getTrayConfig, getVialLabel } from '@/utils/trayHelpers';
  import PopupInfoMethodColumn from '@/uikitProject/popups/info/PopupInfoMethodColumn';
  import PopupHelper from '@/uikitProject/popups/info/PopupHelper.vue';
  import MethodModal from 'components/blocks/reactComponents/MethodModal';
  import InfoColumn from '@/uikitProject/info/InfoColumn';
  import InfoMethod from '@/uikitProject/info/InfoMethod';
  import { openInNewTab } from 'utils/browserHelpers.ts';
  import AppliedPostprocessing from '@/components/blocks/measurements/AppliedPostprocessing.vue';
  import PostprocessingToApply from '@/components/blocks/postprocessings/PostprocessingToApply.vue';
  import { getAvailableDetectorPostprocessings, getDetectorParts } from '@/utils/methodHelpers';

  export default {
    name: 'DeviceMethodColumnComponent',

    components: {
      PostprocessingToApply,
      AppliedPostprocessing,
      MethodModal,
      InfoMethod,
      InfoColumn,
      PopupInfoMethodColumn,
      PopupHelper,
      BtnIcon,
    },

    props: {
      disableClickAway: {
        type: Boolean,
        default: false,
      },
      device: Object,
      configuration: Object,
      method: Object,
      methodOverride: {
        type: Object,
        default: null,
      },
      column: Object,
      postprocessings: Array,
      postprocessingsToApply: Array,
      vial: String,
      sample: {
        type: Object,
      },
      injectionId: {
        type: Number,
      },
      isInSharedMode: {
        type: Boolean,
      },
      isCalibrationSequence: {
        type: Boolean,
      },
    },

    data: () => ({
      methodExpanded: false,
      columnExpanded: false,
      isMethodModalOpen: false,

      postprocessingIdsToShow: {
        // id: true
      },
    }),

    computed: {
      displayVial() {
        const trayConfig =
          getTrayConfig(this.configuration) ?? getTrayConfig(this.device?.configuration);
        return getVialLabel(this.vial, trayConfig);
      },
      linkToDevice() {
        return {
          name: 'device',
          params: { id: this.device.id },
        };
      },
      linkToSequence() {
        if (!this.sample?.sequence) return;
        return {
          name: 'sequence child injection',
          params: {
            id: this.sample.sequence.id,
            sid: this.sample.id,
            iid: this.injectionId,
          },
        };
      },

      isShowSequenceInfo() {
        if (!this.sample?.sequence) return;
        return this.$route.name !== 'sequence child injection';
      },

      postprocessingsToShow() {
        return this.postprocessings?.filter(
          (postprocessing) => this.postprocessingIdsToShow[postprocessing.measurement.id],
        );
      },
      detectorConfiguration() {
        return (
          this.method?.hardware_configuration?.detector?.[0] ??
          this.configuration?.brief?.detector?.[0] ??
          this.device?.configuration?.configuration_for_method?.detector?.[0]
        );
      },
      availablePostprocessingsToApply() {
        return getAvailableDetectorPostprocessings(
          this.postprocessingsToApply,
          this.detectorConfiguration,
        );
      },
      postprocessingsToApplyToShow() {
        return this.availablePostprocessingsToApply.filter(
          ([channel]) => this.postprocessingIdsToShow[channel],
        );
      },

      detectorPortDescription() {
        return this.method?.hardware_configuration?.detector?.[0]?.name;
      },
      methodConfiguration() {
        return this.method?.hardware_configuration;
      },
      parsedMethodOverride() {
        if (!this.methodOverride) {
          return null;
        }

        const result = {};

        if (this.methodOverride.injection_ul != null) {
          result.injectionUl = this.methodOverride.injection_ul;
        }

        if (this.methodOverride.run_time_ms != null) {
          result.runTimeMin = this.methodOverride.run_time_ms / 1000 / 60;
        }

        return result;
      },
    },

    methods: {
      openColumnPage() {
        openInNewTab(`/app/columns/${this.column.id}`);
      },
      openMethodPreviewPage() {
        const injectionIdParam = this.injectionId ? `?injectionId=${this.injectionId}` : '';
        openInNewTab(`/app/methods/preview/${this.method.id}${injectionIdParam}`);
      },

      showPreviewModal() {
        this.isMethodModalOpen = true;
      },

      handleMethodModalClose() {
        this.isMethodModalOpen = false;
      },

      togglePostprocessing(measurementIDorChannel) {
        this.postprocessingIdsToShow = {
          ...this.postprocessingIdsToShow,
          [measurementIDorChannel]: !this.postprocessingIdsToShow[measurementIDorChannel],
        };
      },
      getChannelName(type) {
        return getDetectorParts(this.detectorConfiguration)[type] ?? type;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .device-method-column {
    position: relative;
    background-color: $color-bg-light-selection;
    padding: 8px 22px 8px 16px;
    width: max-content;
    max-width: 100%;
    box-sizing: border-box;

    &__device {
      display: flex;
      max-width: 100%;
      @include textOverflow;
    }

    &__wrapper-link-device {
      flex: 1;
      @include textOverflow;
    }

    &__label {
      color: $color-text-second;
      width: 60px;
      display: inline-block;
      flex-shrink: 0;

      &--wide {
        width: 90px;
      }
    }

    &__modified {
      display: block;
      margin-left: 8px;
      font-size: $size-xxxs;
      color: white;
      background-color: $color-bg-warning;
      padding: 2px 5px;
      border-radius: $border-radius__sm;
      line-height: 1;
      cursor: help;
      user-select: none;
    }

    a,
    &__link {
      display: block;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      max-width: 400px;
      cursor: pointer;
    }

    &__column-title {
      display: flex;
      align-items: center;
      width: fit-content;
      max-width: 100%;
    }

    &__btn-details {
      margin-bottom: 2px;
      margin-left: 4px;
      color: $color-text-primary;
      font-size: 14px;

      &:hover {
        color: $color-text-primary--hover;
      }
    }

    &__btn-open-in-new {
      margin-bottom: 2px;
      margin-left: 4px;
      color: $color-text-primary;
      font-size: 14px;

      &:hover {
        color: $color-text-primary--hover;
      }
    }

    &__measurement {
      white-space: nowrap;
    }

    &__postprocessing-details {
      margin: 12px 0;
    }

    &__btn-edit-postprocessing {
      display: inline;
      margin-left: 4px;
      padding: 0 2px;
      color: $color-text-primary;
      font-size: 14px;

      &:hover {
        color: $color-text-primary--hover;
      }
    }
  }
</style>
